import type {
  PurchaseApprovalItem,
  PurchaseApprovalStatus,
  PurchaseApprovalPurchaseStatus,
} from "@/services/APIs/PtsPurchaseApprovalService";

export const PURCHASE_APPROVAL_PRIORITIES = ["High", "Medium", "Low"] as const;

export const PURCHASE_APPROVAL_CURRENCY_OPTIONS = [
  { value: "INR", label: "INR", symbol: "₹" },
  { value: "US", label: "US", symbol: "$" },
] as const;

export type PurchaseApprovalCurrencyOption =
  (typeof PURCHASE_APPROVAL_CURRENCY_OPTIONS)[number]["value"];

export const DEFAULT_PURCHASE_CURRENCY: PurchaseApprovalCurrencyOption = "INR";

export function currencySymbol(currency?: string | null): string {
  const value = String(currency ?? "").toUpperCase();
  if (value === "US" || value === "USD") {
    return "$";
  }
  return "₹";
}

export const PURCHASE_APPROVAL_STATUS_OPTIONS = [
  "Pending Approval",
  "On-hold",
  "Reject",
  "Approved",
] as const;

export const PURCHASE_STATUS_OPTIONS = [
  "In-progress",
  "Not purchased",
  "Purchased",
] as const;

export type NormalizedStatus =
  | "pending"
  | "on_hold"
  | "approved"
  | "reject"
  | "other";

export function normalizeStatus(
  status?: PurchaseApprovalStatus | null
): NormalizedStatus {
  const value = String(status ?? "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ");

  if (value === "approved" || value === "approval") {
    return "approved";
  }

  if (value === "on hold" || value === "on_hold" || value === "on-hold") {
    return "on_hold";
  }

  if (value === "reject" || value === "rejected") {
    return "reject";
  }

  if (
    value === "pending" ||
    value === "pending approval" ||
    value.includes("pending")
  ) {
    return "pending";
  }

  return "other";
}

export function statusLabel(status?: PurchaseApprovalStatus | null): string {
  const normalized = normalizeStatus(status);
  switch (normalized) {
    case "pending":
      return "Pending Approval";
    case "on_hold":
      return "On-hold";
    case "approved":
      return "Approved";
    case "reject":
      return "Reject";
    default:
      return status ? String(status) : "—";
  }
}

export type NormalizedPurchaseStatus =
  | "in_progress"
  | "not_purchased"
  | "purchased"
  | "other";

export function normalizePurchaseStatus(
  purchaseStatus?: PurchaseApprovalPurchaseStatus | null
): NormalizedPurchaseStatus {
  const value = String(purchaseStatus ?? "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ");

  if (value === "in progress" || value === "in-progress") {
    return "in_progress";
  }
  if (
    value === "not purchased" ||
    value === "not-purchased" ||
    value === "not yet purchased"
  ) {
    return "not_purchased";
  }
  if (value === "purchased") {
    return "purchased";
  }
  return "other";
}

export function purchaseStatusLabel(
  purchaseStatus?: PurchaseApprovalPurchaseStatus | null
): string {
  const normalized = normalizePurchaseStatus(purchaseStatus);
  switch (normalized) {
    case "in_progress":
      return "In-progress";
    case "not_purchased":
      return "Not purchased";
    case "purchased":
      return "Purchased";
    default:
      return purchaseStatus ? String(purchaseStatus) : "—";
  }
}

export function matchesStatusFilter(
  itemStatus: string | null | undefined,
  filterStatus: string | undefined
): boolean {
  if (!filterStatus) {
    return true;
  }
  return normalizeStatus(itemStatus) === normalizeStatus(filterStatus);
}

export function matchesPurchaseStatusFilter(
  itemPurchaseStatus: string | null | undefined,
  filterPurchaseStatus: string | undefined
): boolean {
  if (!filterPurchaseStatus) {
    return true;
  }
  return (
    normalizePurchaseStatus(itemPurchaseStatus) ===
    normalizePurchaseStatus(filterPurchaseStatus)
  );
}

export function filterPurchaseApprovals(
  items: PurchaseApprovalItem[],
  filters: {
    projectId?: string;
    status?: string;
    purchaseStatus?: string;
  }
): PurchaseApprovalItem[] {
  return items.filter(item => {
    if (
      filters.projectId &&
      String(item.project) !== String(filters.projectId)
    ) {
      return false;
    }
    if (!matchesStatusFilter(item.status, filters.status)) {
      return false;
    }
    if (
      !matchesPurchaseStatusFilter(item.purchase_status, filters.purchaseStatus)
    ) {
      return false;
    }
    return true;
  });
}

export function statusBadgeClass(
  status?: PurchaseApprovalStatus | null
): string {
  const normalized = normalizeStatus(status);

  switch (normalized) {
    case "pending":
      return "bg-amber-50 text-amber-700 border-amber-200";

    case "on_hold":
      return "bg-sky-50 text-sky-700 border-sky-200";

    case "approved":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";

    case "reject":
      return "bg-rose-50 text-rose-700 border-rose-200";

    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
}

export function purchaseStatusBadgeClass(
  purchaseStatus?: PurchaseApprovalPurchaseStatus | null
): string {
  const normalized = normalizePurchaseStatus(purchaseStatus);

  switch (normalized) {
    case "in_progress":
      return "bg-amber-50 text-amber-700 border-amber-200";

    case "purchased":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";

    case "not_purchased":
      return "bg-rose-50 text-rose-700 border-rose-200";

    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
}

export function formatInr(value?: number | string | null): string {
  return formatMoney(value, "INR");
}

export function formatMoney(
  value?: number | string | null,
  currency?: string | null
): string {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  const num = Number(value);
  if (Number.isNaN(num)) {
    return String(value);
  }
  const symbol = currencySymbol(currency);
  const locale =
    String(currency ?? "").toUpperCase() === "US" ||
    String(currency ?? "").toUpperCase() === "USD"
      ? "en-US"
      : "en-IN";
  return `${symbol}${num.toLocaleString(locale)}`;
}

export function formatDateYmd(value?: string | null): string {
  if (!value) {
    return "—";
  }
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    return raw.slice(0, 10);
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }
  const yyyy = parsed.getFullYear();
  const mm = String(parsed.getMonth() + 1).padStart(2, "0");
  const dd = String(parsed.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function formatPurchaseApprovalReason(
  item: Pick<PurchaseApprovalItem, "management_reason" | "account_reason">,
  separator = " | "
): string {
  const management = String(item.management_reason ?? "").trim();
  const account = String(item.account_reason ?? "").trim();
  const parts = [management, account].filter(Boolean);
  return parts.length ? parts.join(separator) : "—";
}

export function resolveName(
  idOrName: string | undefined | null,
  lookup: Map<string, string>
): string {
  if (!idOrName) {
    return "—";
  }
  const key = String(idOrName);
  return lookup.get(key) ?? key;
}

export function getMonthBounds(year: number, monthIndex: number) {
  const start = new Date(year, monthIndex, 1);
  const end = new Date(year, monthIndex + 1, 0);
  const toYmd = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { start: toYmd(start), end: toYmd(end) };
}

export function isInMonth(
  dateValue: string | undefined | null,
  year: number,
  monthIndex: number
): boolean {
  if (!dateValue) {
    return false;
  }
  const ymd = formatDateYmd(dateValue);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    return false;
  }
  const [y, m] = ymd.split("-").map(Number);
  return y === year && m === monthIndex + 1;
}

export function monthLabel(year: number, monthIndex: number): string {
  const date = new Date(year, monthIndex, 1);
  return date.toLocaleString("en-US", { month: "long", year: "numeric" });
}

export function monthName(monthIndex: number): string {
  return new Date(2000, monthIndex, 1).toLocaleString("en-US", {
    month: "long",
  });
}
